import { useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { calculateDistance } from "@/utils/distance/calculateDistance";
import type { Facility } from "@/types";

const isMainFacility = (facility: Facility, facilities: Facility[]) => {
    return facilities.some((f) => f.main_facility === facility.id);
};

interface UnitTableFacilityFilterProps {
    facilities: Facility[];
    selectedFacilities: number[];
    onSelectionChange: (facilities: number[]) => void;
}

export const UnitTableFacilityFilter = ({
    facilities,
    selectedFacilities,
    onSelectionChange,
}: UnitTableFacilityFilterProps) => {

    const handleCheckboxChange = (facilityId: number) => {
        const newFacilities = selectedFacilities.includes(facilityId)
            ? selectedFacilities.filter((f) => f !== facilityId)
            : [...selectedFacilities, facilityId];
        onSelectionChange(newFacilities);
    };

    const formatAddress = (facility: Facility) => {
        const parts = [facility.address_1, facility.city, facility.state, facility.zipcode].filter(Boolean);
        return parts.join(", ");
    };

    const sortedFacilities = useMemo(() => {
        const mainFacility = facilities.find((f) => isMainFacility(f, facilities));
        if (!mainFacility) return facilities;

        return [...facilities].sort((a, b) => {
            // Main facility always first
            if (isMainFacility(a, facilities)) return -1;
            if (isMainFacility(b, facilities)) return 1;

            // Calculate distance from main facility
            const distanceA = calculateDistance(
                mainFacility.latitude,
                mainFacility.longitude,
                a.latitude,
                a.longitude
            );

            const distanceB = calculateDistance(
                mainFacility.latitude,
                mainFacility.longitude,
                b.latitude,
                b.longitude
            );

            return distanceA - distanceB;
        });
    }, [facilities]);

    const getDistanceFromMain = (facility: Facility) => {
        const mainFacility = facilities.find((f) => isMainFacility(f, facilities));
        if (!mainFacility || isMainFacility(facility, facilities)) return 0;

        return calculateDistance(
            mainFacility.latitude,
            mainFacility.longitude,
            facility.latitude,
            facility.longitude
        );
    };

    if (facilities.length <= 1) {
        return null;
    }

    return (
        <div>
            <p className="tw:font-bold">By Location</p>
            {sortedFacilities.map((facility) => {
                const distance = getDistanceFromMain(facility);
                return (
                    <div
                        key={facility.id}
                        className="tw:items-start tw:flex tw:flex-row tw:space-x-3 tw:space-y-0 tw:py-2"
                    >
                        <Checkbox
                            id={`facility-${facility.id}`}
                            onCheckedChange={() => handleCheckboxChange(facility.id)}
                            checked={selectedFacilities.includes(facility.id)}
                            className="tw:mt-0.5"
                        />
                        <Label
                            htmlFor={`facility-${facility.id}`}
                            className="tw:text-sm tw:font-normal tw:ml-2 tw:flex tw:flex-col tw:gap-0.5"
                        >
                            <span className="tw:font-bold">
                                {isMainFacility(facility, facilities) ? "Main Location" : facility.name}
                            </span>
                            {!isMainFacility(facility, facilities) && distance > 0 && (
                                <span>
                                    ({distance.toFixed(1)} mi from main location)
                                </span>
                            )}
                            <span>
                                {formatAddress(facility)}
                            </span>
                        </Label>
                    </div>
                );
            })}
        </div>
    );
};
